import { UserDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { DOCUMENT_STATUS_LABELS, DocumentStatus } from '../enums/document-status.enum';
import { DocumentPublicationFlowState } from '../enums/document-publication-flow-state.enum';
import { IDocument, IDocumentOnNode } from '../models/document.model';

type DocumentLinkInput = Pick<IDocumentOnNode, 'id' | 'status' | 'versionId'> & {
  deleted?: boolean;
  enabled?: boolean;
};

type PublicationStateDocumentLinkInput = Pick<IDocument, 'id' | 'deleted' | 'enabled'> &
  Partial<Pick<IDocument, 'newestVersionId' | 'isPublished' | 'publicationFlowState'>>;

export interface DocumentLinkResult {
  link: string;
  referrerType: string;
}

const EXTERNAL_DOCUMENT_BY_ID_PATH =
  /^\/documents\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;
const EXTERNAL_DOCUMENT_BY_ID_LIKE_PATH = /^\/documents\/([0-9a-f-]{32,36})\/?$/i;
const PUBLICATION_FLOW_STATE_TO_DOCUMENT_STATUS: Record<
  DocumentPublicationFlowState,
  DocumentStatus
> = {
  [DocumentPublicationFlowState.ForApproval]: DocumentStatus.ForApproval,
  [DocumentPublicationFlowState.OpenChangeRequest]: DocumentStatus.ChangeRequest,
  [DocumentPublicationFlowState.UnderRevision]: DocumentStatus.UnderRevision,
  [DocumentPublicationFlowState.Rejected]: DocumentStatus.Rejected,
};

export function getLinkToDocument(document: DocumentLinkInput): DocumentLinkResult {
  if (document.deleted) {
    return buildDocumentLink(document.id, DocumentStatus.Deleted);
  }

  if (document.enabled === false) {
    return buildDocumentLink(document.id, DocumentStatus.Disabled);
  }

  if (
    document.status === DocumentStatus.Published ||
    document.status === DocumentStatus.Expired ||
    document.status === DocumentStatus.Expiring
  ) {
    return buildDocumentLink(document.id, DocumentStatus.Published, document.versionId);
  }

  return buildDocumentLink(document.id, document.status);
}

export function getLinkToDocumentByPublicationState(
  document: PublicationStateDocumentLinkInput
): DocumentLinkResult {
  if (document.deleted) {
    return buildDocumentLink(document.id, DocumentStatus.Deleted);
  }

  if (document.enabled === false) {
    return buildDocumentLink(document.id, DocumentStatus.Disabled);
  }

  const publicationStateStatus = document.publicationFlowState
    ? PUBLICATION_FLOW_STATE_TO_DOCUMENT_STATUS[document.publicationFlowState]
    : undefined;
  if (publicationStateStatus !== undefined) {
    return buildDocumentLink(document.id, publicationStateStatus);
  }

  if (document.isPublished) {
    return buildDocumentLink(document.id, DocumentStatus.Published, document.newestVersionId);
  }

  return buildDocumentLink(document.id, DocumentStatus.Draft);
}

export function isExternalDocumentByIdPath(path: string | null | undefined): boolean {
  return !!getExternalDocumentIdFromPath(path);
}

export function getExternalDocumentIdFromPath(path: string | null | undefined): string | null {
  if (!path) {
    return null;
  }

  return (
    path.match(EXTERNAL_DOCUMENT_BY_ID_PATH)?.[1] ??
    path.match(EXTERNAL_DOCUMENT_BY_ID_LIKE_PATH)?.[1] ??
    null
  );
}

function buildDocumentLink(
  documentId: string,
  status: DocumentStatus,
  versionId?: string
): DocumentLinkResult {
  const referrerType = DOCUMENT_STATUS_LABELS[status];
  const versionSegment = versionId ? `/${versionId}` : '';

  return {
    link: `${UserDocumentsRoutes.Main}/${referrerType}/${documentId}${versionSegment}`,
    referrerType,
  };
}
